import { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MoreVertical, CalendarDays, AlertCircle, ChevronRight, MessageSquare, Trash2, Globe, EyeOff, ImagePlus, ArrowUpCircle, Send, Check, Undo2, Receipt, Facebook, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { mediaRawUrl, API } from "@/lib/api";
import { setWebsiteVisibility, fmtD, countdown, contractChip, adChip, TONE, AD_NOT_STARTED, CONTRACT_NOT_STARTED } from "@/lib/pipelineApi";
import { TestBadge } from "@/components/TestMode";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth";
import { isAdminRole } from "@/lib/access";
import { badgeText } from "@/lib/accountingApi";
import { MarkPostedDialog, canMarkPosted, isPosted } from "./MarkPosted";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useSandbox } from "@/lib/sandbox";

// Informational only: seller overrode "Horse Already Exists". Shows and opens the previously entered horse.
export function DuplicateFlag({ c, testId }) {
  const navigate = useNavigate();
  const d = c.duplicate;
  if (!d) return null;
  const stop = (e) => e.stopPropagation();
  const path = d.horse_id ? `/horse/${d.horse_id}` : d.application_id ? `/applications/${d.application_id}` : null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`${testId}-duplicate`} onClick={stop} onKeyDown={stop}
          className="inline-flex items-center gap-1 h-6 px-2 rounded-full border border-red-300 bg-red-50 text-[11px] font-semibold text-red-800 hover:bg-red-100">
          <AlertCircle className="h-3 w-3" />Duplicate
        </button>
      </PopoverTrigger>
      <PopoverContent onClick={stop} data-testid={`${testId}-duplicate-info`} className="w-72 text-sm">
        <p className="font-semibold text-stone-900">Previously entered horse</p>
        <p className="mt-1 text-stone-700"><strong>{d.name || "Unnamed horse"}</strong>{d.status ? ` · ${d.status}` : ""}</p>
        <p className="mt-1 text-xs text-stone-500">Matched on {d.match === "registered" ? "registered name / number" : "barn name"}. The seller chose to submit a new application.</p>
        {path && <button type="button" data-testid={`${testId}-duplicate-open`} onClick={() => navigate(path)}
          className="mt-3 h-9 px-4 rounded-full bg-stone-900 text-white text-xs font-semibold">Open existing record</button>}
      </PopoverContent>
    </Popover>
  );
}

// Pipeline page provides requestDelete(card); only Admins/Super Admins see the menu item (server enforces too).
export const DeleteHorseCtx = createContext(null);
export const PipelineReloadCtx = createContext(null);
// (c, files?) opens the Add Media modal; null when the user can't upload.
export const AddMediaCtx = createContext(null);
// (c) opens Move to LIVE; Super Admins only (server enforces too).
export const MoveLiveCtx = createContext(null);

// Files dragged from the desktop onto a card: highlight only, never open/move the card or upload.
function useFileDrop(c) {
  const openMedia = useContext(AddMediaCtx);
  const [over, setOver] = useState(false);
  if (!openMedia || !c.ids.horse_id) return [{}, false];
  const hasFiles = (e) => [...(e.dataTransfer?.types || [])].includes("Files");
  const on = (e) => { if (!hasFiles(e)) return; e.preventDefault(); e.stopPropagation(); setOver(true); };
  return [{
    onDragEnter: on, onDragOver: on,
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(false); },
    onDrop: (e) => { if (!hasFiles(e)) return; e.preventDefault(); e.stopPropagation(); setOver(false); openMedia(c, [...e.dataTransfer.files]); },
  }, over];
}

const DropHint = ({ testId }) => (
  <div data-testid={`${testId}-drop-hint`} className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] bg-amber-50/85 border-2 border-dashed border-amber-700 text-amber-900 text-xs font-bold">
    <ImagePlus className="h-4 w-4 mr-1.5" />Drop to add photos &amp; videos
  </div>
);

// Display-only Title Case: lowercase words get a capital; words already containing capitals (acronyms, registered names) are kept.
export const titleName = (s) => (s ? String(s).replace(/[^\s\-']+/g, (w) => (w === w.toLowerCase() ? w.charAt(0).toUpperCase() + w.slice(1) : w)) : s);

export function ReserveLine({ c, testId }) {
  if (!c.reserve) return null;
  return <div data-testid={`${testId}-reserve`} className={`text-[11px] font-semibold truncate ${c.reserve.kind === "unset" ? "text-stone-500" : "text-stone-800"}`}>Reserve: {c.reserve.label}</div>;
}

export function CardPhoto({ c, className = "" }) {
  const [failed, setFailed] = useState(false);
  const p = c.photo;
  const src = p && (p.external_url || (p.attachment_id && mediaRawUrl(p.horse_id, p.attachment_id))
    || (p.historical_cover && `${API}/horses/${p.horse_id}/historical-cover`)
    || (p.media_id && `${API}/applications/${p.application_id}/media/${p.media_id}/file${p.rotation ? `?r=${p.rotation}` : ""}`));
  if (src && !failed) return <img src={src} alt={c.name} loading="lazy" decoding="async" onError={() => setFailed(true)} className={`object-cover bg-stone-100 ${className}`} />;
  return <div className={`flex items-center justify-center bg-stone-200 border border-stone-300 text-stone-600 font-display font-bold text-xl ${className}`}>{(c.name || "?").slice(0, 1).toUpperCase()}</div>;
}

export function StatusChip({ chip, testId }) {
  if (!chip) return null;
  return <span data-testid={testId} className={`inline-flex items-center h-6 px-2 rounded-full border text-[11px] font-semibold whitespace-nowrap ${TONE[chip[1]]}`}>{chip[0]}</span>;
}

// The single most relevant status for a card in its stage.
export const primaryChip = (c) => (c.kind === "lead" ? [c.status || "Lead", "stone"]
  : c.stage === "approvals" ? [c.status, "sky"]
  : c.stage === "contracts_ads" ? (c.contract_status && c.contract_status !== "signed" ? contractChip(c) : adChip(c) || contractChip(c))
  : adChip(c) || [c.status, "stone"]);

// Approved applications: contract and ad are separate parts of the workflow, so show both.
const splitChips = (c) => c.kind === "application" && c.stage !== "approvals" && c.application_status === "approved";

export function WorkflowChips({ c, testId }) {
  if (!splitChips(c)) return <StatusChip chip={primaryChip(c)} testId={`${testId}-primary`} />;
  return (
    <>
      <StatusChip chip={contractChip(c) || CONTRACT_NOT_STARTED} testId={`${testId}-contract`} />
      <StatusChip chip={adChip(c) || AD_NOT_STARTED} testId={`${testId}-ad`} />
    </>
  );
}

export const saleLine = (c) => {
  const d = c.dates || {};
  if (c.stage === "ongoing" && d.closes_at_utc) return `Closes ${fmtD(d.close_date)} · ${countdown(d.closes_at_utc)}`;
  if (c.sale_record?.sold_at) return `Sold ${fmtD(c.sale_record.sold_at)}`;
  return d.close_date ? `Closes ${fmtD(d.close_date)}` : null;
};

// Scheduled sale dates under the name/seller; shown only when set.
export function ScheduleLines({ c, testId }) {
  const d = c.dates || {};
  if (!d.bidding_starts && !d.close_date) return null;
  return (
    <div data-testid={`${testId}-schedule`} className="mt-1.5 text-[11px] text-stone-600 leading-snug">
      {d.facebook_post_date && <div className="flex items-center gap-1 truncate"><CalendarDays className="h-3 w-3 shrink-0" /><span className="truncate">Posting: {fmtD(d.facebook_post_date)}{d.ad_weeks ? ` · ${d.ad_weeks} wk` : ""}</span></div>}
      {d.bidding_starts && <div className="flex items-center gap-1 truncate"><CalendarDays className="h-3 w-3 shrink-0" /><span className="truncate">Bidding Starts: {fmtD(d.bidding_starts)}</span></div>}
      {d.close_date && <div className="flex items-center gap-1 truncate"><CalendarDays className="h-3 w-3 shrink-0" /><span className="truncate">Bidding Ends: {fmtD(d.close_date)}{c.stage === "ongoing" && d.closes_at_utc && !c.awaiting_finalization ? ` · ${countdown(d.closes_at_utc)}` : ""}</span></div>}
    </div>
  );
}

export function HiddenIcon({ c, testId }) {
  if (!c.website_hidden) return null;
  return <EyeOff data-testid={testId} role="img" aria-label="Hidden from Website" className="inline h-3.5 w-3.5 shrink-0 text-stone-400 align-[-2px]"><title>Hidden from Website</title></EyeOff>;
}

export function CardMenu({ c, onOpen, testId }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const requestDelete = useContext(DeleteHorseCtx);
  const canDelete = requestDelete && c.kind !== "lead" && isAdminRole(user);
  const { load: reload, patch, openAccounting } = useContext(PipelineReloadCtx) || {};
  const openMedia = useContext(AddMediaCtx);
  const requestMove = useContext(MoveLiveCtx);
  const canMove = requestMove && c.is_test && c.kind !== "lead" && user?.role === "super_admin";
  const [saving, setSaving] = useState(false);
  const [postDlg, setPostDlg] = useState(null);
  const admin = isAdminRole(user);
  const sandbox = useSandbox();
  const hidden = !!c.website_hidden;
  const toggleWebsite = async () => {
    if (saving) return;
    setSaving(true); patch?.(c.key, { website_hidden: !hidden });
    try {
      await setWebsiteVisibility(c.ids.horse_id, hidden);
      toast.success(hidden ? "Shown on website (when eligible)" : "Hidden from website");
      reload?.();
    } catch (e) { patch?.(c.key, { website_hidden: hidden }); toast.error(e?.response?.data?.detail || "Could not update website visibility"); }
    setSaving(false);
  };
  return (
    <>
    {postDlg !== null && <div onClick={(e) => e.stopPropagation()}><MarkPostedDialog c={c} posted={postDlg} onClose={() => setPostDlg(null)} onDone={() => { setPostDlg(null); reload?.(); }} /></div>}
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid={testId} onClick={(e) => e.stopPropagation()} aria-label="Horse actions"
          className="h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-full text-stone-600 hover:bg-stone-100"><MoreVertical className="h-4 w-4" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem data-testid={`${testId}-open`} onSelect={onOpen}>Open Horse Workspace</DropdownMenuItem>
        {c.ids.application_id && <DropdownMenuItem data-testid={`${testId}-application`} onSelect={() => navigate(`/applications/${c.ids.application_id}`)}>Open Application & Contract</DropdownMenuItem>}
        {admin && openAccounting && c.kind !== "lead" && (sandbox || (c.ids.horse_id && c.ids.application_id)) && <DropdownMenuItem data-testid={`${testId}-accounting`} onSelect={() => openAccounting(c.key)}><Receipt className="h-4 w-4 mr-1.5" />Open Accounting</DropdownMenuItem>}
        {c.ids.horse_id && <DropdownMenuItem data-testid={`${testId}-ad`} onSelect={() => navigate(`/horse/${c.ids.horse_id}`)}>Open Ad Builder</DropdownMenuItem>}
        {c.facebook_url && <DropdownMenuItem onSelect={() => window.open(c.facebook_url, "_blank", "noopener")}>View Facebook Post</DropdownMenuItem>}
        {c.ids.horse_id && openMedia && <DropdownMenuItem data-testid={`${testId}-add-media`} onSelect={() => openMedia(c, null)}><ImagePlus className="h-4 w-4 mr-1.5" />Add Media</DropdownMenuItem>}
        {c.ids.horse_id && <DropdownMenuItem data-testid={`${testId}-website`} disabled={saving} onSelect={toggleWebsite}>
          {hidden ? <EyeOff className="h-4 w-4 mr-1.5" /> : <Globe className="h-4 w-4 mr-1.5" />}Show on Website: {hidden ? "Off" : "On"}</DropdownMenuItem>}
        {admin && canMarkPosted(c) && <DropdownMenuItem data-testid={`${testId}-mark-posted`} onSelect={() => setPostDlg(true)}><Send className="h-4 w-4 mr-1.5" />Mark as Posted</DropdownMenuItem>}
        {admin && isPosted(c) && <>
          <DropdownMenuItem data-testid={`${testId}-posted`} disabled><Check className="h-4 w-4 mr-1.5 text-emerald-700" />Posted</DropdownMenuItem>
          <DropdownMenuItem data-testid={`${testId}-correct-posted`} onSelect={() => setPostDlg(false)}><Undo2 className="h-4 w-4 mr-1.5" />Correct Posted Status</DropdownMenuItem>
        </>}
        {canMove && <><DropdownMenuSeparator /><DropdownMenuItem data-testid={`${testId}-move-live`} onSelect={() => requestMove(c)} className="text-emerald-800 focus:text-emerald-900"><ArrowUpCircle className="h-4 w-4 mr-1.5" />Move to LIVE</DropdownMenuItem></>}
        {canDelete && <><DropdownMenuSeparator /><DropdownMenuItem data-testid={`${testId}-delete`} onSelect={() => requestDelete(c)} className="text-red-700 focus:text-red-800"><Trash2 className="h-4 w-4 mr-1.5" />Delete Horse</DropdownMenuItem></>}
      </DropdownMenuContent>
    </DropdownMenu>
    </>
  );
}

const alertOf = (c) => (c.application_status === "more_info_requested" || c.listing_status === "changes_requested" || c.ad_template_status === "changes_requested" ? c.next_action : null);

export function UnreadBadge({ n, testId }) {
  if (!n) return null;
  return (
    <span data-testid={testId} title={`${n} unread seller message${n === 1 ? "" : "s"}`}
      className="inline-flex items-center gap-1 rounded-full bg-stone-800 text-white px-2 py-0.5 text-[11px] font-bold shrink-0">
      <MessageSquare className="h-3 w-3" />{n}
    </span>
  );
}

// Compact fee status (payment once invoiced). Admin-only data; shown only after the contract is signed.
function AccountingBadges({ a, testId }) {
  const tone = (s) => (s === "Paid" ? "text-emerald-800" : ["Ready to Invoice", "Overdue", "Error"].includes(s) ? "text-orange-800" : "text-stone-700");
  return (
    <div data-testid={testId} className="mt-1.5 flex items-center gap-x-2 gap-y-0.5 flex-wrap text-[11px] leading-tight">
      <Receipt className="h-3 w-3 shrink-0 text-stone-500" />
      {[["Initial", a.initial], ["Final", a.final]].map(([l, b]) => { const s = badgeText(b); return <span key={l} data-testid={`${testId}-${l.toLowerCase()}`} className={`font-semibold ${tone(s)}`}>{l}: {s}</span>; })}
    </div>
  );
}

// Opens the horse's Facebook GROUP post (existing publishing.facebook_group URL, where bidding happens). Never the Page post.
export function FacebookBidsLink({ c, testId }) {
  if (!c.facebook_url) return null;
  return (
    <a data-testid={testId} href={c.facebook_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}
      className="mt-1.5 inline-flex items-center gap-1 h-7 px-2.5 rounded-full border border-blue-200 bg-blue-50 text-[11px] font-semibold text-blue-800 hover:bg-blue-100">
      <Facebook className="h-3.5 w-3.5" />View Facebook Bids
    </a>
  );
}

export const AwaitingChip = ({ c, testId }) => (c.awaiting_finalization ? <StatusChip chip={["Bidding Closed — Awaiting Finalization", "orange"]} testId={testId} /> : null);

// Business Page status + link, kept separate from the Group post link above.
export function FacebookPageLink({ c, testId }) {
  if (!c.facebook_page_url) return null;
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px]">
      <span data-testid={`${testId}-status`} className="font-semibold text-emerald-800">Facebook Page: Posted</span>
      <a data-testid={testId} href={c.facebook_page_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full border border-stone-300 bg-white font-semibold text-stone-800 hover:bg-stone-50">
        <Briefcase className="h-3.5 w-3.5" />Open Facebook Page Post
      </a>
    </div>
  );
}

export function PipelineCard({ c, onOpen, testId }) {
  const sale = saleLine(c);
  const alert = alertOf(c);
  const [drop, over] = useFileDrop(c);
  return (
    <div role="button" tabIndex={0} data-testid={testId} onClick={onOpen} onKeyDown={(e) => e.key === "Enter" && onOpen()} {...drop}
      className={`relative w-full text-left bg-white border rounded-xl shadow-sm hover:shadow-md transition-[box-shadow,border-color] cursor-pointer overflow-hidden ${over ? "border-amber-700 ring-2 ring-amber-600" : "border-stone-200 hover:border-stone-300"}`}>
      {over && <DropHint testId={testId} />}
      <CardPhoto c={c} className="h-28 w-full" />
      <div className="p-2.5">
        <div className="flex items-start gap-1">
          <div className="min-w-0 flex-1">
              {c.is_test && <TestBadge testId={`${testId}-test`} />}
              <div className="font-semibold text-sm text-stone-900 leading-snug line-clamp-2 break-words">{titleName(c.name)} <HiddenIcon c={c} testId={`${testId}-hidden`} /></div>
              {c.details && <div className="text-[11px] text-stone-600 truncate">{c.details}</div>}
              {c.owner && <div data-testid={`${testId}-owner`} className="text-[11px] text-stone-600 truncate">{titleName(c.owner)}</div>}
              <ReserveLine c={c} testId={testId} />
              <ScheduleLines c={c} testId={testId} />
          </div>
          <CardMenu c={c} onOpen={onOpen} testId={`${testId}-menu`} />
        </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`${testId}-status`}>
        <DuplicateFlag c={c} testId={testId} />
        <AwaitingChip c={c} testId={`${testId}-awaiting`} />
        <WorkflowChips c={c} testId={testId} />
        <UnreadBadge n={c.unread_messages} testId={`${testId}-unread`} />
      </div>
      {c.accounting && <AccountingBadges a={c.accounting} testId={`${testId}-acct`} />}
      <FacebookBidsLink c={c} testId={`${testId}-fb-bids`} />
      <FacebookPageLink c={c} testId={`${testId}-fb-page`} />
      {c.sale_record?.sold_at && <div className="mt-1.5 flex items-center gap-1 text-[11px] text-stone-600"><CalendarDays className="h-3 w-3 shrink-0" /><span className="truncate">{sale}</span></div>}
      {alert && <div className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-orange-800"><AlertCircle className="h-3 w-3 shrink-0" /><span className="truncate">{alert}</span></div>}
      <div className="sr-only" data-testid={`${testId}-next`}>{c.next_action}</div>
      </div>
    </div>
  );
}

export function StageRow({ c, onOpen, testId }) {
  const sale = saleLine(c);
  const [drop, over] = useFileDrop(c);
  return (
    <div role="button" tabIndex={0} data-testid={testId} onClick={onOpen} onKeyDown={(e) => e.key === "Enter" && onOpen()} {...drop}
      className={`relative flex items-center gap-3 px-3 py-2.5 hover:bg-stone-50 active:bg-stone-100 cursor-pointer ${over ? "bg-amber-50 ring-2 ring-inset ring-amber-600" : "bg-white"}`}>
      {over && <DropHint testId={testId} />}
      <CardPhoto c={c} className="h-14 w-16 shrink-0 rounded-lg" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 min-w-0">
          {c.is_test && <TestBadge testId={`${testId}-test`} />}
          <span className="font-semibold text-sm text-stone-900 truncate">{titleName(c.name)}</span>
          <HiddenIcon c={c} testId={`${testId}-hidden`} />
        </div>
        <div data-testid={`${testId}-owner`} className="text-[11px] text-stone-600 truncate">{[c.details, titleName(c.owner)].filter(Boolean).join(" · ")}</div>
        <ReserveLine c={c} testId={testId} />
        <ScheduleLines c={c} testId={testId} />
        <div className="mt-1 flex flex-wrap items-center gap-1.5 min-w-0" data-testid={`${testId}-status`}>
          <DuplicateFlag c={c} testId={testId} />
          <AwaitingChip c={c} testId={`${testId}-awaiting`} />
          <WorkflowChips c={c} testId={testId} />
          <UnreadBadge n={c.unread_messages} testId={`${testId}-unread`} />
          {c.sale_record?.sold_at && <span className="text-[11px] text-stone-600 truncate">{sale}</span>}
        </div>
        {c.accounting && <AccountingBadges a={c.accounting} testId={`${testId}-acct`} />}
        <FacebookBidsLink c={c} testId={`${testId}-fb-bids`} />
      <FacebookPageLink c={c} testId={`${testId}-fb-page`} />
      </div>
      <div onClick={(e) => e.stopPropagation()}><CardMenu c={c} onOpen={onOpen} testId={`${testId}-menu`} /></div>
      <ChevronRight className="h-4 w-4 text-stone-400 shrink-0" />
    </div>
  );
}
